<template>
    <div class="peer-history-card w-100">
        <AonCard
            class="h-100 display-flex flex-column"
            style="position: relative"
            :title="`Peer History in ${props.nodeData.displayName}`"
        >
            <AonCoverLoading
                :loading="loadingData"
                title="Loading Peer History..."
            ></AonCoverLoading>
            <div class="content-holder flex-grow-1">
                <div class="actions display-flex align-items-center mb-sm">
                    <AonButton
                        class="table-button"
                        :class="{ 'active-table-button': activeSideBarType === 'filters' }"
                        label="Filters"
                        text-with-icon
                        type="subtle"
                        :icon-options="{ iconStyle: 'fas', iconName: 'fa-bars-filter' }"
                        @clicked="() => toggleSideBar('filters', gridApi)"
                    />
                    <AonButton
                        class="ml-md table-button"
                        :class="{ 'active-table-button': activeSideBarType === 'columns' }"
                        label="Columns"
                        text-with-icon
                        type="subtle"
                        :icon-options="{ iconStyle: 'fas', iconName: 'fa-objects-column' }"
                        @clicked="() => toggleSideBar('columns', gridApi)"
                    />
                    <div
                        class="clear-filters display-flex align-items-center cursor-pointer p-sm ml-sm"
                        @click="clearFilters"
                    >
                        <font-awesome-icon :icon="`fas fa-xmark`" class="text-moat-600" />
                        <p class="geist-font text-moat-600 bold ml-xs">Clear Filters</p>
                    </div>
                </div>
                <MoatTable
                    style="height: 83%"
                    class="w-100"
                    :class="themeClass"
                    :column-defs="colDefs"
                    :suppress-auto-size="true"
                    :row-data="tableData"
                    :autoSizeStrategy="sizingStrategy"
                    :sorting-order="['desc', 'asc', null]"
                    :enable-charts="true"
                    :cell-selection="true"
                    :side-bar="sideBar"
                    @grid-ready="gridInit"
                />
            </div>
        </AonCard>
    </div>
</template>

<script setup>
import { onMounted, ref, inject } from 'vue'
import { useEntityStore } from '@/stores'
import { getEntitiesLitigationSummary } from '@/api/ipNode.js'
import { useI18n } from 'vue-i18n'
import { useFlag } from '@unleash/proxy-client-vue'

import { AgGridVue as MoatTable } from '@ag-grid-community/vue3'
import { useAgGridSidebar } from '@/composables/useAgGridSidebar.js'

const props = defineProps({
    nodeData: {
        type: Object,
        default: () => {},
    },
    title: {
        type: String,
        default: '',
    },
    description: {
        type: String,
        default: '',
    },
})

const showCrunchbaseArchOnly = useFlag('ipAlpha.archUsersOnly')

const logger = inject('logger')
const filters = inject('filters')
const entityStore = useEntityStore()
const { t } = useI18n()

const loadingData = ref(true)
const tableData = ref([])
const {
    sideBar,
    activeSideBarType,
    sideBarOpen,
    toggleSideBar,
    injectSidebarHeaders,
    clearFilters: sidebarClearFilters,
} = useAgGridSidebar({
    panels: [
        {
            id: 'columns',
            label: 'Columns',
            width: 275,
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
                suppressRowGroups: true,
                suppressValues: true,
                suppressPivotMode: true,
            },
        },
        {
            id: 'filters',
            label: 'Filters',
            width: 275,
            toolPanel: 'agFiltersToolPanel',
        },
    ],
    injectHeaders: {
        filters: 'Filters',
        columns: 'Columns',
    },
    position: 'left',
})
const sizingStrategy = ref({})
const axisRange = ref([0, 10])

const gridApi = ref(null)
const themeClass = ref('ag-theme-quartz')
const colDefs = ref([
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefintion: t('productAlignment.definitionTooltips.companyName'),
            headerName: 'Company Name',
            showFilterButton: true,
        },
        cellRenderer: 'MTCustomCellCompany',
        cellRendererParams: {
            allowNewTab: true,
            tabOnLeft: true,
        },
        field: 'entityName',
        headerName: 'Company Name',
        headerTooltip: 'Company Name',
        minWidth: 300,
        sortingOrder: ['asc', 'desc', null],
        filter: 'agTextColumnFilter',
        pinned: 'left',
        lockPosition: true,
        chartDataType: 'category',
    },
    {
        field: 'competitorRank',
        headerName: 'Rank',
        valueFormatter: (p) => {
            if (p.data) {
                return `${p.data.competitorRank.toLocaleString()}`
            }
        },
        headerTooltip: 'Rank',
        minWidth: 120,
        sortingOrder: ['asc', 'desc', null],
        filter: 'agNumberColumnFilter',
        chartDataType: 'category',
    },
    {
        field: 'caseCountHistory',
        headerTooltip: 'Case Count By Year',
        cellRenderer: 'agSparklineCellRenderer',
        cellRendererParams: {
            sparklineOptions: {
                type: 'column',
                xKey: 'year',
                yKey: 'caseCount',
                fill: '#2A79D2',
                valueAxisDomain: axisRange.value,
                axis: { type: 'number' },
                tooltip: {
                    renderer: (params) => {
                        const { x, y } = params.datum
                        return {
                            title: `${x}:`,
                            content: y,
                        }
                    },
                },
            },
        },
    },
    {
        field: 'casesCount',
        headerName: 'Case Count',
        headerTooltip: 'Case Count',
        minWidth: 150,
        filter: 'agNumberColumnFilter',
        valueFormatter: (p) => {
            if (p.data) {
                return `${p.data.casesCount.toLocaleString()}`
            }
        },
        chartDataType: 'series',
    },
    {
        field: 'casesDefendantCount',
        headerName: 'Defendant Case Count',
        headerTooltip: 'Defendant Case Count',
        minWidth: 240,
        filter: 'agNumberColumnFilter',
        valueFormatter: (p) => {
            if (p.data) {
                return `${p.data.casesDefendantCount.toLocaleString()}`
            }
        },
        chartDataType: 'series',
    },
    {
        field: 'casesPlaintiffCount',
        headerName: 'Plaintiff Case Count',
        headerTooltip: 'Plaintiff Case Count',
        minWidth: 240,
        filter: 'agNumberColumnFilter',
        valueFormatter: (p) => {
            if (p.data) {
                return `${p.data.casesPlaintiffCount.toLocaleString()}`
            }
        },
        chartDataType: 'series',
    },
    {
        field: 'casesNPECount',
        headerName: 'NPE Case Count',
        headerTooltip: 'NPE Case Count',
        minWidth: 240,
        filter: 'agNumberColumnFilter',
        valueFormatter: (p) => {
            if (p.data) {
                return `${p.data.casesNPECount.toLocaleString()}`
            }
        },
        chartDataType: 'series',
    },
    {
        field: 'avgCaseLength',
        headerName: 'Average Case Length',
        headerTooltip: 'Average Case Length',
        flex: 1,
        minWidth: 220,
        valueFormatter: (p) => {
            if (p.data) {
                return p.data.avgCaseLength.toLocaleString()
            }
        },
    },
    {
        field: 'damagesIncurred',
        headerTooltip: 'Damages Paid',
        flex: 1,
        minWidth: 120,
        valueFormatter: (p) => {
            if (p.data) {
                return filters.abbreviate(p.data.damagesIncurred)
            }
        },
    },
])

onMounted(() => {
    getTopInnovations()
})

const gridInit = (params) => {
    // If we start added thing for different user groups, make a function to set this
    if (showCrunchbaseArchOnly.value) {
        colDefs.value.splice(2, 0, {
            field: 'crunchbaseRank',
            headerName: 'Crunchbase Rank',
            valueFormatter: (p) => {
                if (p.data) {
                    return p.data.crunchbaseRank ? p.data.crunchbaseRank.toLocaleString() : '--'
                }
            },
            filter: 'agNumberColumnFilter',
            chartDataType: 'category',
        })
    }

    gridApi.value = params.api
    injectSidebarHeaders()
}

const getTopInnovations = async () => {
    loadingData.value = true
    try {
        let { data } = await getEntitiesLitigationSummary(props.nodeData.id)
        tableData.value = data.results
    } catch (error) {
        logger.error(error)
    } finally {
        loadingData.value = false
    }
}

const clearFilters = () => {
    if (gridApi.value) {
        sidebarClearFilters(gridApi.value)
    }
}
</script>

<style lang="scss" scoped>
.peer-history-card {
    height: 500px;
    position: relative;
}
</style>
